/**
 * Payment Service
 * Handles all payment-related API calls (Razorpay integration)
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class PaymentService {
  /**
   * Create Razorpay order
   * @param {string} serviceType - Type of service: "medicine_order", "doctor_appointment", "medical_test", "paramedical_service", "online_consultation"
   * @param {number} serviceId - ID of the booked service/order
   * @returns {Promise<Object>} Response with Razorpay order details { razorpay_order_id, amount, currency, key }
   */
  async createPaymentOrder(serviceType, serviceId) {
    try {
      const response = await axios.post(API_ENDPOINTS.PAYMENT.CREATE_ORDER, {
        service_type: serviceType,
        service_id: serviceId,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Poll payment status after Razorpay callback
   * @param {string} razorpayOrderId - Razorpay order ID from callback
   * @returns {Promise<Object>} { success, razorpay_order_id, status, amount, ... }
   */
  async getPaymentStatus(razorpayOrderId) {
    try {
      const response = await axios.get(API_ENDPOINTS.PAYMENT.STATUS(razorpayOrderId));
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Initialize Razorpay payment gateway
   * @param {Object} options - Razorpay options
   * @param {string} options.key - Razorpay key from server
   * @param {number} options.amount - Amount in paise
   * @param {string} options.currency - Currency code (default: INR)
   * @param {string} options.razorpay_order_id - Razorpay order ID
   * @param {string} options.name - Business name
   * @param {string} options.description - Order description
   * @param {string} options.image - Business logo URL
   * @param {Object} options.prefill - User prefill data
   * @param {Function} options.handler - Success handler function
   * @param {Object} options.notes - Additional notes
   * @returns {void} Opens Razorpay modal
   */
  initializeRazorpay(options) {
    if (typeof window.Razorpay === 'undefined') {
      throw new Error('Razorpay SDK not loaded. Please include the Razorpay script.');
    }

    const razorpayOptions = {
      key: options.key,
      amount: options.amount,
      currency: options.currency || 'INR',
      order_id: options.razorpay_order_id, // Changed from options.order_id
      name: options.name || 'HomeCure Express',
      description: options.description || 'Payment',
      image: options.image || '',
      handler: options.handler || this.defaultPaymentHandler,
      prefill: {
        name: options.prefill?.name || '',
        email: options.prefill?.email || '',
        contact: options.prefill?.contact || '',
      },
      notes: options.notes || {},
      theme: {
        color: '#104a5f', // Primary brand color
      },
      modal: {
        ondismiss: options.onDismiss || (() => {
          console.log('Payment modal closed');
        }),
      },
    };

    const razorpay = new window.Razorpay(razorpayOptions);
    razorpay.open();
  }

  /**
   * Default payment success handler
   * @param {Object} response - Razorpay response
   */
  defaultPaymentHandler(response) {
    console.log('Payment successful:', response);
  }

  /**
   * Convert rupees to paise
   * @param {number} rupees - Amount in rupees
   * @returns {number} Amount in paise
   */
  convertToPaise(rupees) {
    return Math.round(rupees * 100);
  }

  /**
   * Convert paise to rupees
   * @param {number} paise - Amount in paise
   * @returns {number} Amount in rupees
   */
  convertToRupees(paise) {
    return paise / 100;
  }

  /**
   * Format currency
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency symbol (default: ₹)
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount, currency = '₹') {
    return `${currency}${amount.toFixed(2)}`;
  }

  /**
   * Load Razorpay script dynamically
   * @returns {Promise<void>} Resolves when script is loaded
   */
  loadRazorpayScript() {
    return new Promise((resolve, reject) => {
      if (typeof window.Razorpay !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
      document.body.appendChild(script);
    });
  }

  /**
   * Complete payment flow
   * @param {string} serviceType - Service type: "medicine_order", "doctor_appointment", "medical_test", "paramedical_service", "online_consultation"
   * @param {number} serviceId - Service ID
   * @param {Object} userInfo - User information
   * @param {Function} onSuccess - Success callback (receives payment status data)
   * @param {Function} onError - Error callback
   * @returns {Promise<void>}
   */
  async processPayment(serviceType, serviceId, userInfo, onSuccess, onError) {
    try {
      // Load Razorpay script if not already loaded
      await this.loadRazorpayScript();

      // Create payment order — server calculates the amount
      const orderData = await this.createPaymentOrder(serviceType, serviceId);

      // Initialize Razorpay
      this.initializeRazorpay({
        key: orderData.key,
        amount: orderData.amount, // In paise from backend
        razorpay_order_id: orderData.razorpay_order_id,
        prefill: {
          name: userInfo.name,
          email: userInfo.email,
          contact: userInfo.mobile,
        },
        handler: async (response) => {
          try {
            // Poll GET /payment/status/<razorpay_order_id>/ — backend confirms via webhook
            const maxAttempts = 6;
            const delayMs = 2000;

            let statusData;
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              statusData = await this.getPaymentStatus(response.razorpay_order_id);

              if (statusData.status === 'captured') {
                if (onSuccess) onSuccess(statusData);
                return;
              }

              if (statusData.status === 'failed') {
                throw new Error('Payment failed on server side.');
              }

              if (attempt < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
              }
            }

            throw new Error('Payment verification timed out. If money was deducted, contact support.');
          } catch (error) {
            if (onError) onError(error);
          }
        },
        onDismiss: () => {
          if (onError) onError(new Error('Payment cancelled by user'));
        },
      });
    } catch (error) {
      if (onError) onError(error);
      throw error;
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage = error.response.data.error || error.response.data.message || 'Payment failed';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new PaymentService();
